import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { haptic } from '@/lib/haptics'

interface GuidedCameraScreenProps {
  onCapture: (blob: Blob) => void
  onCancel: () => void
  onError: (message: string) => void
}

/**
 * Simplified camera screen - just oval guide and capture button
 * No face detection, works consistently on iOS and Android
 */
export function GuidedCameraScreen({ onCapture, onCancel, onError }: GuidedCameraScreenProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const [cameraReady, setCameraReady] = useState(false)

  // Initialize camera
  useEffect(() => {
    let mounted = true

    const initCamera = async () => {
      try {
        console.log('[Camera] Requesting camera...')

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        })

        if (!mounted) {
          stream.getTracks().forEach(t => t.stop())
          return
        }

        streamRef.current = stream
        console.log('[Camera] Got stream')

        const video = videoRef.current
        if (!video) {
          console.error('[Camera] No video element!')
          return
        }

        video.srcObject = stream

        // Wait for video to be ready
        const handleCanPlay = () => {
          console.log('[Camera] Video can play')
          video.play()
            .then(() => {
              console.log('[Camera] Video playing!')
              if (mounted) setCameraReady(true)
            })
            .catch(e => {
              console.error('[Camera] Play failed:', e)
              // Try muted
              video.muted = true
              video.play()
                .then(() => {
                  console.log('[Camera] Video playing (muted)')
                  if (mounted) setCameraReady(true)
                })
                .catch(e2 => {
                  console.error('[Camera] Still failed:', e2)
                  onError('Cannot play video. Please try again.')
                })
            })
        }

        video.addEventListener('canplay', handleCanPlay, { once: true })

        // Fallback timeout
        setTimeout(() => {
          if (mounted && !cameraReady && video.readyState >= 2) {
            console.log('[Camera] Using timeout fallback')
            setCameraReady(true)
          }
        }, 3000)

      } catch (error) {
        console.error('[Camera] Camera error:', error)
        if (error instanceof DOMException) {
          if (error.name === 'NotAllowedError') {
            onError('Camera permission denied.')
          } else if (error.name === 'NotFoundError') {
            onError('No camera found.')
          } else {
            onError(`Camera error: ${error.message}`)
          }
        } else {
          onError('Failed to access camera.')
        }
      }
    }

    initCamera()

    return () => {
      mounted = false
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [onError])

  const capturePhoto = useCallback(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Mirror the image (selfie mode)
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, 0, 0)

    canvas.toBlob(
      (blob) => {
        if (blob) {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
          }
          onCapture(blob)
        } else {
          onError('Failed to capture photo')
        }
      },
      'image/jpeg',
      0.92
    )
  }, [onCapture, onError])

  const handleCapture = () => {
    haptic('heavy')
    capturePhoto()
  }

  return (
    <div className="flex-1 flex flex-col bg-black relative overflow-hidden">
      {/* Video element */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Loading overlay */}
        <AnimatePresence>
          {!cameraReady && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black flex flex-col items-center justify-center z-10"
            >
              <div className="w-16 h-16 mb-6 relative">
                <div className="absolute inset-0 border-4 border-gray-700 rounded-full" />
                <div className="absolute inset-0 border-4 border-[#46C0C6] rounded-full border-t-transparent animate-spin" />
              </div>
              <p className="text-white text-lg">Starting camera...</p>
              <button
                onClick={() => { haptic('light'); onCancel() }}
                className="mt-8 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Face Oval Guide */}
        {cameraReady && (
          <div className="absolute inset-0 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <mask id="oval-mask">
                  <rect width="100" height="100" fill="white" />
                  <ellipse cx="50" cy="45" rx="33" ry="44" fill="black" />
                </mask>
              </defs>
              <rect width="100" height="100" fill="rgba(0,0,0,0.5)" mask="url(#oval-mask)" />
              <ellipse
                cx="50" cy="45" rx="33" ry="44"
                fill="none"
                stroke="#ffffff"
                strokeWidth="0.5"
                strokeDasharray="2,2"
              />
            </svg>
          </div>
        )}

        {/* Top instruction */}
        {cameraReady && (
          <div className="absolute top-4 left-0 right-0 flex justify-center">
            <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
              <p className="text-white text-sm font-medium">
                Position your face in the oval and tap to capture
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      {cameraReady && (
        <div className="bg-black px-4 py-6 flex items-center justify-between">
          <button
            onClick={() => { haptic('light'); onCancel() }}
            className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center"
          >
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={handleCapture}
            className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center"
          >
            <div className="w-14 h-14 rounded-full bg-white" />
          </button>

          <div className="w-12 h-12" />
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}

// Export types for compatibility
export type QualityStatus = 'checking' | 'good' | 'warning' | 'error'

export interface QualityCheck {
  id: string
  label: string
  status: QualityStatus
  message: string
}
